import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { notify, getAdminProfile } from '../../../utils/notifications';

const generateInvoiceNumber = async (orgId) => {
  const { count } = await supabase?.from('invoices')?.select('id', { count: 'exact', head: true })?.eq('org_id', orgId);
  const num = String((count || 0) + 1)?.padStart(4, '0');
  return `WLC-${num}`;
};

const InvoiceBuilder = ({ job, isOpen, onClose, orgName, orgPhone, orgEmail }) => {
  const { profile } = useAuth();
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [taxRate, setTaxRate] = useState(0);
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [notes, setNotes] = useState('Payment due within 30 days');
  const [saving, setSaving] = useState(false);
  const [savedInvoices, setSavedInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState('builder');

  useEffect(() => {
    if (isOpen && job) {
      // Pre-populate from job
      const name = job?.client
        ? `${job?.client?.first_name || ''} ${job?.client?.last_name || ''}`?.trim()
        : '';
      setClientName(name);
      setClientPhone(job?.client?.phone || '');
      setClientEmail(job?.client?.email || '');

      const items = [];
      if (Number(job?.labor_cost) > 0) {
        items?.push({ description: job?.service_type || 'Service', qty: 1, unit_price: Number(job?.labor_cost) || 0 });
      }
      if (Number(job?.materials_cost) > 0) {
        items?.push({ description: 'Materials', qty: 1, unit_price: Number(job?.materials_cost) || 0 });
      }
      if (items?.length === 0) {
        items?.push({ description: job?.service_type || 'Service', qty: 1, unit_price: 0 });
      }
      setLineItems(items);

      if (job?.tax_included) {
        setTaxEnabled(true);
        setTaxRate(Number(job?.tax_rate) || 10);
      } else {
        setTaxEnabled(false);
        setTaxRate(0);
      }

      generateInvoiceNumber(profile?.org_id)?.then(setInvoiceNumber);
      fetchInvoices();
    }
  }, [isOpen, job]);

  const fetchInvoices = async () => {
    if (!job?.id) return;
    const { data } = await supabase?.from('invoices')?.select('*')?.eq('job_id', job?.id)?.order('created_at', { ascending: false });
    setSavedInvoices(data || []);
  };

  const subtotal = lineItems?.reduce((sum, item) => sum + (Number(item?.qty) || 0) * (Number(item?.unit_price) || 0), 0);
  const taxAmount = taxEnabled ? subtotal * (taxRate / 100) : 0;
  const total = subtotal + taxAmount;

  const addLineItem = () => {
    setLineItems(prev => [...prev, { description: '', qty: 1, unit_price: 0 }]);
  };

  const updateLineItem = (index, field, value) => {
    setLineItems(prev => prev?.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const removeLineItem = (index) => {
    setLineItems(prev => prev?.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase?.from('invoices')?.insert({
        org_id: profile?.org_id,
        job_id: job?.id,
        invoice_number: invoiceNumber,
        client_name: clientName,
        client_phone: clientPhone,
        client_email: clientEmail,
        line_items: lineItems,
        subtotal,
        tax_rate: taxEnabled ? taxRate : 0,
        tax_amount: taxAmount,
        total,
        notes,
        status: 'draft',
      });
      if (error) throw error;
      toast?.success('Invoice saved!');
      fetchInvoices();
      setActiveTab('invoices');
    } catch (err) {
      toast?.error('Failed to save invoice: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  const previewInvoice = () => {
    const html = buildInvoiceHTML({
      invoiceNumber, clientName, clientPhone, clientEmail,
      lineItems, subtotal, taxRate: taxEnabled ? taxRate : 0, taxAmount, total, notes,
      orgName, orgPhone, orgEmail, jobTitle: job?.title, jobDate: job?.scheduled_date
    });
    const w = window.open('', '_blank');
    w?.document?.write(html);
    w?.document?.close();
  };

  const printInvoice = () => {
    const html = buildInvoiceHTML({
      invoiceNumber, clientName, clientPhone, clientEmail,
      lineItems, subtotal, taxRate: taxEnabled ? taxRate : 0, taxAmount, total, notes,
      orgName, orgPhone, orgEmail, jobTitle: job?.title, jobDate: job?.scheduled_date
    });
    const w = window.open('', '_blank');
    w?.document?.write(html);
    w?.document?.close();
    w?.print();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-2xl bg-background rounded-2xl shadow-2xl overflow-hidden mx-4 max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Receipt" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Invoice Builder</h2>
              <p className="text-xs text-muted-foreground">{job?.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {['builder', 'invoices']?.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-sm font-medium capitalize transition-colors ${
                activeTab === tab ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab === 'invoices' ? `Invoices (${savedInvoices?.length})` : 'Create Invoice'}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          {activeTab === 'builder' && (
            <div className="p-6 space-y-5">
              {/* Invoice Number */}
              <div className="flex items-center justify-between bg-primary/5 rounded-lg px-4 py-3">
                <span className="text-sm font-medium text-foreground">Invoice #</span>
                <span className="text-sm font-bold text-primary">{invoiceNumber}</span>
              </div>

              {/* Client Info */}
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Client Information</h3>
                <div className="grid grid-cols-1 gap-3">
                  <input
                    type="text"
                    value={clientName}
                    onChange={e => setClientName(e?.target?.value)}
                    placeholder="Client name"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="tel"
                      value={clientPhone}
                      onChange={e => setClientPhone(e?.target?.value)}
                      placeholder="Phone"
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                    />
                    <input
                      type="email"
                      value={clientEmail}
                      onChange={e => setClientEmail(e?.target?.value)}
                      placeholder="Email"
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                    />
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Line Items</h3>
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted text-xs font-medium text-muted-foreground">
                    <div className="col-span-6">Description</div>
                    <div className="col-span-2 text-center">Qty</div>
                    <div className="col-span-3 text-right">Unit Price</div>
                    <div className="col-span-1" />
                  </div>
                  {lineItems?.map((item, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-border items-center">
                      <div className="col-span-6">
                        <input
                          type="text"
                          value={item?.description}
                          onChange={e => updateLineItem(i, 'description', e?.target?.value)}
                          placeholder="Description"
                          className="w-full text-sm bg-transparent focus:outline-none"
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          min="1"
                          value={item?.qty}
                          onChange={e => updateLineItem(i, 'qty', e?.target?.value)}
                          className="w-full text-sm text-center bg-transparent focus:outline-none"
                        />
                      </div>
                      <div className="col-span-3">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item?.unit_price}
                          onChange={e => updateLineItem(i, 'unit_price', e?.target?.value)}
                          className="w-full text-sm text-right bg-transparent focus:outline-none"
                        />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button onClick={() => removeLineItem(i)} className="text-muted-foreground hover:text-red-500 transition-colors">
                          <Icon name="X" size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="px-3 py-2 border-t border-border">
                    <button
                      onClick={addLineItem}
                      className="flex items-center space-x-1 text-xs text-primary hover:underline"
                    >
                      <Icon name="Plus" size={12} />
                      <span>Add line item</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Totals */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Subtotal</span>
                  <span>${subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setTaxEnabled(p => !p)}
                      className={`relative w-8 h-4 rounded-full transition-colors ${taxEnabled ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                    >
                      <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${taxEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                    <span className="text-sm text-muted-foreground">Tax</span>
                    {taxEnabled && (
                      <div className="flex items-center space-x-1">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={taxRate}
                          onChange={e => setTaxRate(Number(e?.target?.value))}
                          className="w-12 text-xs text-center border border-border rounded px-1 py-0.5 bg-background"
                        />
                        <span className="text-xs text-muted-foreground">%</span>
                      </div>
                    )}
                  </div>
                  <span className="text-sm text-muted-foreground">{taxEnabled ? `$${taxAmount?.toFixed(2)}` : '$0.00'}</span>
                </div>
                <div className="flex justify-between text-base font-bold text-foreground border-t border-border pt-2">
                  <span>Total</span>
                  <span className="text-primary">${total?.toFixed(2)}</span>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Notes</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e?.target?.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  onClick={previewInvoice}
                  className="flex items-center space-x-2 px-4 py-2 border border-border rounded-lg text-sm hover:bg-muted transition-colors"
                >
                  <Icon name="Eye" size={15} />
                  <span>Preview</span>
                </button>
                <button
                  onClick={printInvoice}
                  className="flex items-center space-x-2 px-4 py-2 border border-border rounded-lg text-sm hover:bg-muted transition-colors"
                >
                  <Icon name="Download" size={15} />
                  <span>Download PDF</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 ml-auto"
                >
                  <Icon name="Save" size={15} />
                  <span>{saving ? 'Saving...' : 'Save Invoice'}</span>
                </button>
              </div>
            </div>
          )}

          {activeTab === 'invoices' && (
            <div className="p-6">
              {savedInvoices?.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="Receipt" size={32} className="text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No invoices yet</p>
                  <button
                    onClick={() => setActiveTab('builder')}
                    className="text-xs text-primary hover:underline mt-2"
                  >
                    Create first invoice
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {savedInvoices?.map(inv => (
                    <div key={inv?.id} className="border border-border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-semibold text-foreground">{inv?.invoice_number}</span>
                          <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                            inv?.status === 'paid' ? 'bg-green-100 text-green-700' :
                            inv?.status === 'sent'? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                          }`}>{inv?.status}</span>
                        </div>
                        <span className="text-sm font-bold text-primary">${Number(inv?.total)?.toFixed(2)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{inv?.client_name}</p>
                      <p className="text-xs text-muted-foreground">{new Date(inv.created_at)?.toLocaleDateString()}</p>
                      <div className="flex space-x-2 mt-3">
                        <button
                          onClick={() => {
                            const html = buildInvoiceHTML({
                              invoiceNumber: inv?.invoice_number,
                              clientName: inv?.client_name,
                              clientPhone: inv?.client_phone,
                              clientEmail: inv?.client_email,
                              lineItems: inv?.line_items || [],
                              subtotal: inv?.subtotal,
                              taxRate: inv?.tax_rate,
                              taxAmount: inv?.tax_amount,
                              total: inv?.total,
                              notes: inv?.notes,
                              orgName, orgPhone, orgEmail,
                              jobTitle: job?.title,
                            });
                            const w = window.open('', '_blank');
                            w?.document?.write(html);
                            w?.document?.close();
                            w?.print();
                          }}
                          className="flex-1 py-1.5 text-xs bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                        >
                          Print/PDF
                        </button>
                        <button
                          onClick={async () => {
                            await supabase?.from('invoices')?.update({ status: 'paid' })?.eq('id', inv?.id);
                            fetchInvoices();
                            toast?.success('Marked as paid!');

                            // Send in-app + email notification
                            try {
                              const adminProfile = await getAdminProfile(profile?.org_id);
                              await notify({
                                org_id: profile?.org_id,
                                type: 'payment_received',
                                title: `Payment Received: $${Number(inv?.total)?.toFixed(2)}`,
                                body: `Invoice ${inv?.invoice_number || ''} — ${clientName || ''}`,
                                link: '/jobs',
                                adminEmail: adminProfile?.email || null,
                                orgName: orgName || adminProfile?.full_name || 'there',
                                emailData: {
                                  amount: `$${Number(inv?.total)?.toFixed(2)}`,
                                  invoice_number: inv?.invoice_number || '',
                                  client_name: clientName || '',
                                },
                              });
                            } catch (notifErr) {
                              console.error('Failed to send payment notification:', notifErr);
                            }
                          }}
                          disabled={inv?.status === 'paid'}
                          className="flex-1 py-1.5 text-xs border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-40"
                        >
                          Mark Paid
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

const buildInvoiceHTML = ({ invoiceNumber, clientName, clientPhone, clientEmail, lineItems, subtotal, taxRate, taxAmount, total, notes, orgName, orgPhone, orgEmail, jobTitle, jobDate }) => {
  const date = new Date()?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const dueDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const lineItemsHTML = (lineItems || [])?.map(item => `
    <tr>
      <td style="padding:10px 12px;border-bottom:1px solid #f0f0f0;">${item?.description || ''}</td>
      <td style="padding:10px 12px;border-bottom:1px solid #f0f0f0;text-align:center;">${item?.qty || 1}</td>
      <td style="padding:10px 12px;border-bottom:1px solid #f0f0f0;text-align:right;">$${Number(item?.unit_price || 0)?.toFixed(2)}</td>
      <td style="padding:10px 12px;border-bottom:1px solid #f0f0f0;text-align:right;font-weight:600;">$${(Number(item?.qty || 1) * Number(item?.unit_price || 0))?.toFixed(2)}</td>
    </tr>
  `)?.join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Invoice ${invoiceNumber}</title>
  <style>
    @media print { body { margin: 0; } .no-print { display: none; } }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #1a1a1a; background: #fff; }
    .container { max-width: 700px; margin: 40px auto; padding: 40px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; padding-bottom: 30px; border-bottom: 2px solid #f0f0f0; }
    .company-name { font-size: 24px; font-weight: 700; color: #1a1a1a; }
    .company-info { font-size: 13px; color: #666; margin-top: 6px; line-height: 1.6; }
    .invoice-badge { background: #7C3AED; color: white; padding: 6px 16px; border-radius: 20px; font-size: 12px; font-weight: 600; letter-spacing: 1px; }
    .invoice-number { font-size: 28px; font-weight: 700; color: #7C3AED; text-align: right; margin-top: 8px; }
    .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 35px; }
    .meta-block h4 { font-size: 11px; font-weight: 600; color: #999; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px; }
    .meta-block p { font-size: 14px; color: #333; line-height: 1.6; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    thead tr { background: #f8f7ff; }
    thead th { padding: 12px; text-align: left; font-size: 12px; font-weight: 600; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
    thead th:last-child, thead th:nth-child(3), thead th:nth-child(2) { text-align: right; }
    thead th:nth-child(2) { text-align: center; }
    .totals { margin-left: auto; width: 260px; }
    .totals-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 14px; color: #555; }
    .totals-total { display: flex; justify-content: space-between; padding: 12px 0; font-size: 18px; font-weight: 700; color: #1a1a1a; border-top: 2px solid #7C3AED; margin-top: 6px; }
    .totals-total span:last-child { color: #7C3AED; }
    .notes { margin-top: 30px; padding: 16px; background: #f8f7ff; border-radius: 8px; border-left: 3px solid #7C3AED; }
    .notes p { font-size: 13px; color: #555; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #f0f0f0; text-align: center; font-size: 12px; color: #999; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <div>
        <div class="company-name">${orgName || 'Your Company'}</div>
        <div class="company-info">
          ${orgPhone ? orgPhone + '<br>' : ''}
          ${orgEmail ? orgEmail : ''}
        </div>
      </div>
      <div style="text-align:right;">
        <div class="invoice-badge">INVOICE</div>
        <div class="invoice-number">${invoiceNumber}</div>
      </div>
    </div>

    <div class="meta">
      <div class="meta-block">
        <h4>Bill To</h4>
        <p><strong>${clientName || 'Client'}</strong><br>
        ${clientPhone ? clientPhone + '<br>' : ''}
        ${clientEmail ? clientEmail : ''}</p>
      </div>
      <div class="meta-block">
        <h4>Invoice Details</h4>
        <p>
          <strong>Date:</strong> ${date}<br>
          <strong>Due Date:</strong> ${dueDate}<br>
          ${jobTitle ? '<strong>Job:</strong> ' + jobTitle : ''}
        </p>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th>Description</th>
          <th style="text-align:center;">Qty</th>
          <th style="text-align:right;">Unit Price</th>
          <th style="text-align:right;">Total</th>
        </tr>
      </thead>
      <tbody>
        ${lineItemsHTML}
      </tbody>
    </table>

    <div class="totals">
      <div class="totals-row"><span>Subtotal</span><span>$${Number(subtotal)?.toFixed(2)}</span></div>
      ${Number(taxRate) > 0 ? `<div class="totals-row"><span>Tax (${taxRate}%)</span><span>$${Number(taxAmount)?.toFixed(2)}</span></div>` : ''}
      <div class="totals-total"><span>Total</span><span>$${Number(total)?.toFixed(2)}</span></div>
    </div>

    ${notes ? `<div class="notes"><p>${notes}</p></div>` : ''}

    <div class="footer">
      <p>Thank you for your business! — ${orgName || 'Your Company'}</p>
    </div>
  </div>
</body>
</html>`;
};

export default InvoiceBuilder;
